/**
 * jtbdStudyIndex — maps JTBD selections to a studies carousel start index.
 *
 * Each JTBD value maps to a prioritized list of study type IDs.
 * We score study types by frequency across the selected JTBDs, then find
 * the carousel window starting position that maximises the top-scored type.
 *
 * Returns 0 (carousel default) when no JTBDs are selected.
 */

/** Ordered list of study type IDs matching CANONICAL_STUDY_CARDS in StudiesSection */
const STUDY_TYPE_ORDER = [
  'concept-testing',
  'brand-perception',
  'customer-discovery',
  'ad-messaging',
  'pricing-packaging',
] as const

type StudyTypeId = (typeof STUDY_TYPE_ORDER)[number]

/** Static JTBD → priority study type mapping */
const JTBD_MAP: Record<string, Array<StudyTypeId>> = {
  'Understand my consumers':       ['customer-discovery', 'brand-perception'],
  'Build a stronger brand':        ['brand-perception', 'ad-messaging'],
  'Test concepts & creative':      ['concept-testing', 'ad-messaging'],
  'Find the right audience':       ['customer-discovery', 'concept-testing'],
  'Explore markets & trends':      ['customer-discovery', 'pricing-packaging'],
  'Improve products & experience': ['concept-testing', 'pricing-packaging'],
}

/**
 * Computes the carousel `initialIndex` for StudiesSection from the given
 * JTBD selections.
 *
 * @param selectedJtbd - Array of selected JTBD values (empty = skipped)
 * @returns Zero-based index into CANONICAL_STUDY_CARDS
 */
export function computeJtbdStudyIndex(selectedJtbd: Array<string>): number {
  if (selectedJtbd.length === 0) return 0

  // Tally frequency of each study type across all selected JTBDs
  const scores: Record<string, number> = {}
  for (const jtbd of selectedJtbd) {
    const studyTypes = JTBD_MAP[jtbd] ?? []
    studyTypes.forEach((st) => {
      scores[st] = (scores[st] ?? 0) + 1
    })
  }

  // Find the highest-scoring study type
  let bestType: StudyTypeId | null = null
  let bestScore = -1
  for (const [type, score] of Object.entries(scores)) {
    if (score > bestScore) {
      bestScore = score
      bestType = type as StudyTypeId
    }
  }

  if (!bestType) return 0

  const idx = STUDY_TYPE_ORDER.indexOf(bestType)
  return idx === -1 ? 0 : idx
}
